import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View, StyleSheet, FlatList, } from 'react-native';
import Alert from 'utils/packages/Alert';
import TouchableBounce from 'utils/packages/TouchableBounce';
import emoji from 'node-emoji';
import Animated from 'react-native-reanimated';
import { deleteGroupUser, renameGroup, deleteGroup, leaveGroup } from 'actions';
import * as types from 'actions/types';
import { AppView, CardView, SettingsItem, SettingsItemRightContent, SettingsSection, Spacer, SCROLL_INDICATOR_INSETS, SettingsItemNavigate, } from 'design-system';
import { hitSlop, rem } from 'design-system/values';
import { HEIGHT } from 'design-system/Button';
import Section from 'design-system/Section';
import { IconTrash } from 'design-system/icons';
import { IconShareSquare } from 'design-system/icons/IconShareSquare';
import { IconLink } from 'design-system/icons/IconLink';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import { selectExtraMembersInfo, selectIsExtraMembersFeatureAvailable, } from 'reducers/selectors/sharedPlans';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectGroupAnalytics, selectGroupById, selectNavigatorConfig, } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { ShareGroupModal } from 'features/group/components/ShareGroupModal';
import ExtraMembersIntroCard from 'features/premium/components/ExtraMembersIntroCard';
import IconThreeUser from 'design-system/icons/IconThreeUser';
import Text from 'design-system/Text';
import { focus } from 'hooks/useAutoFocusInput';
import { GroupUserAvatar } from './components/GroupExpenseCRow';
const GroupUser = ({ user, onPress, currUser, createdBy, }) => {
    const colors = useColors();
    return (<SettingsItem renderIcon={() => <GroupUserAvatar groupUser={user}/>} title={`${user?.firstName || '?'} ${user?.lastName || ''}`} description={`@${user.handle}`}>
      <SettingsItemRightContent>
        {createdBy === user.id ? (<Text Text-14 Gray-50>
            Owner
          </Text>) : ((currUser.id === user.id || createdBy === currUser.id) && (<TouchableBounce hitSlop={hitSlop} onPress={onPress}>
              <IconTrash gradientColors={[
                colors.elements.secondary,
                colors.elements.secondary,
            ]}/>
            </TouchableBounce>))}
      </SettingsItemRightContent>
    </SettingsItem>);
};
const GroupSettingsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { groupId } = route.params;
    const colors = useColors();
    const dispatch = useAppDispatch();
    const { navigateToTab } = useTabNavigation();
    const user = useAppSelector((store) => store.user.user);
    const { showPayTab } = useAppSelector(selectNavigatorConfig);
    const groupAnalytics = useAppSelector((store) => selectGroupAnalytics(store, groupId));
    const group = useAppSelector((store) => selectGroupById(store, groupId));
    const filteredUsers = useMemo(() => groupAnalytics?.users?.filter((u) => !u.deleted), [groupAnalytics]);
    const isCurrentUserAdmin = user.id === group?.createdBy;
    const onDelete = useCallback(() => {
        Alert.alert('Delete the group', 'Are you sure you want to delete this group?', [
            {
                text: 'Yes',
                onPress: () => {
                    dispatch(deleteGroup(groupId)).then((result) => {
                        if (result?.type === types.DELETE_GROUP_SUCCESS) {
                            navigation.pop(2);
                        }
                    });
                },
                style: 'destructive',
            },
            {
                text: 'Cancel',
                style: 'cancel',
            },
        ], {
            cancelable: true,
        });
    }, [groupId]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => isCurrentUserAdmin ? (<NavigationHeaderRightIconButton onPress={onDelete}>
              <IconTrash gradientColors={[
                    colors.elements.primary,
                    colors.elements.primary,
                ]}/>
            </NavigationHeaderRightIconButton>) : null,
        });
    }, [colors.elements.primary, user, isCurrentUserAdmin, onDelete]);
    const onDeleteUser = useCallback((userId) => {
        Alert.alert(emoji.emojify('Hold on :dizzy:'), isCurrentUserAdmin
            ? 'Are you sure you want to delete this member?'
            : 'Are you sure you want to leave this group?', [
            {
                text: 'No',
                onPress: () => { },
                style: 'cancel',
            },
            {
                text: 'Yes',
                style: 'destructive',
                onPress: async () => {
                    if (isCurrentUserAdmin) {
                        // admin removing user
                        dispatch(deleteGroupUser(groupId, userId));
                    }
                    else {
                        // user leaving group
                        const res = await dispatch(deleteGroupUser(groupId, userId));
                        if (res?.type === types.DELETE_GROUP_USER_SUCCESS) {
                            dispatch(leaveGroup(groupId));
                            if (showPayTab) {
                                navigateToTab('Pay');
                            }
                            else {
                                navigation.navigate('Feed');
                            }
                        }
                    }
                },
            },
        ]);
    }, [groupId, isCurrentUserAdmin, navigateToTab, showPayTab]);
    const onRename = useCallback(async (name, setLoading, inputRef) => {
        if (!name.trim().length) {
            Alert.alert(emoji.emojify('Hold on :dizzy:'), 'You must enter a name.', [{ text: 'Okay', onPress: () => focus(inputRef) }]);
            return;
        }
        setLoading(true);
        await dispatch(renameGroup(groupId, group?.name, name));
        setLoading(false);
        navigation.pop();
    }, [groupId, group?.name]);
    const onRenamePress = useCallback(() => {
        navigation.navigateDeprecated('TextFieldPage', {
            title: 'Group name',
            defaultValue: group?.name,
            onPressNext: onRename,
            buttonTitle: 'Save',
        });
    }, [group?.name, onRename]);
    const shareGroup = useCallback(() => {
        if (!group)
            return;
        ShareGroupModal.show({
            onInviteContacts: () => navigation.navigateDeprecated('GroupInvite', { group }),
            group,
        });
    }, [group]);
    const ListHeaderComponent = useMemo(() => (<SettingsItem title="Add members" icon={<IconLink color={colors.elements.brand}/>} onPress={shareGroup}>
          <SettingsItemRightContent>
            <IconShareSquare gradientColors={[
            colors.elements.secondary,
            colors.elements.secondary,
        ]}/>
          </SettingsItemRightContent>
        </SettingsItem>), [colors.elements.brand, colors.elements.secondary, shareGroup]);
    const renderItem = useCallback(({ item }) => (<GroupUser createdBy={group?.createdBy} currUser={user} key={String(item?.id)} user={item} onPress={() => onDeleteUser(item?.id)}/>), [group?.createdBy, onDeleteUser, user]);
    const isExtraMembersFeatureAvailable = useAppSelector(selectIsExtraMembersFeatureAvailable);
    const extraMembersInfo = useAppSelector(selectExtraMembersInfo);
    const numOfOthers = filteredUsers ? filteredUsers.length - 1 : 0;
    const showExtraMembersIntroCard = isExtraMembersFeatureAvailable &&
        numOfOthers > 0 &&
        !(extraMembersInfo && !extraMembersInfo.canAddSlot);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16));
    return (<AppView withFrame={false}>
        <Animated.ScrollView style={styles.scrollingContent} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="handled">
          <View style={paddingHorizontalStyle}>
            <SettingsSection>
              <SettingsItemNavigate description="Name" title={group?.name} icon={<IconThreeUser gradientKey="orange"/>} onPress={onRenamePress}/>
            </SettingsSection>
            <Spacer.H24 />

            <Section title="Members">
              <CardView style={styles.cardView}>
                <FlatList data={filteredUsers} ListHeaderComponent={ListHeaderComponent} renderItem={renderItem} scrollEnabled={false}/>
              </CardView>
            </Section>
            <Spacer.H16 />

            {showExtraMembersIntroCard ? (<Section title={'Add members into your plan'}>
                <ExtraMembersIntroCard suggestGroupId={groupId} suggestNumOfSlots={numOfOthers}/>
              </Section>) : null}
          </View>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: 'Group settings',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    cardView: {
        marginVertical: 0,
    },
    scrollingContent: {
        width: '100%',
        flex: 1,
        marginVertical: rem(-24),
        paddingTop: rem(12),
    },
});
export default GroupSettingsScreen;
